import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

const CHAPTER_PARAM = 'chapter';
const SECTIONS_PARAM = 'sections';

export const toSectionKey = (chapterId: string, sectionId: string): string =>
    `${chapterId}:${sectionId}`;

export const parseSectionIdsFromUrl = (sectionsParam: string | null, chapterId: string | null): Set<string> => {
    if (!sectionsParam || !chapterId) {
        return new Set();
    }

    const sectionIds = sectionsParam
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean);

    return new Set(sectionIds.map((sectionId) => toSectionKey(chapterId, sectionId)));
};

export const encodeSectionIdsForUrl = (openSectionIds: Set<string>, chapterId: string | null): string[] => {
    if (!chapterId) {
        return [];
    }

    const prefix = `${chapterId}:`;
    return [...openSectionIds]
        .filter((key) => key.startsWith(prefix))
        .map((key) => key.slice(prefix.length));
};

interface UseJourneyAccordionParamsResult {
    openChapterId: string | null;
    openSectionIds: Set<string>;
    handleToggleChapter: (chapterId: string) => void;
    handleToggleSection: (chapterId: string, sectionId: string) => void;
    syncFromLoadedChapters: (chapterIds: string[]) => void;
    pruneSectionsForChapter: (chapterId: string, validSectionIds: string[]) => void;
}

export const useJourneyAccordionParams = (): UseJourneyAccordionParamsResult => {
    const [searchParams, setSearchParams] = useSearchParams();

    const openChapterId = searchParams.get(CHAPTER_PARAM);
    const openSectionIds = useMemo(
        () => parseSectionIdsFromUrl(searchParams.get(SECTIONS_PARAM), openChapterId),
        [searchParams, openChapterId],
    );

    const updateParams = useCallback(
        (chapterId: string | null, sectionIds: Set<string>) => {
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);

                    if (chapterId) {
                        next.set(CHAPTER_PARAM, chapterId);
                        const encodedSections = encodeSectionIdsForUrl(sectionIds, chapterId);
                        if (encodedSections.length > 0) {
                            next.set(SECTIONS_PARAM, encodedSections.join(','));
                        } else {
                            next.delete(SECTIONS_PARAM);
                        }
                    } else {
                        next.delete(CHAPTER_PARAM);
                        next.delete(SECTIONS_PARAM);
                    }

                    return next;
                },
                { replace: true },
            );
        },
        [setSearchParams],
    );

    const handleToggleChapter = useCallback(
        (chapterId: string) => {
            const isClosing = openChapterId === chapterId;
            if (isClosing) {
                updateParams(null, new Set());
                return;
            }

            const sectionsForChapter = new Set(
                [...openSectionIds].filter((key) => key.startsWith(`${chapterId}:`)),
            );
            updateParams(chapterId, sectionsForChapter);
        },
        [openChapterId, openSectionIds, updateParams],
    );

    const handleToggleSection = useCallback(
        (chapterId: string, sectionId: string) => {
            const sectionKey = toSectionKey(chapterId, sectionId);
            const nextSectionIds = new Set(openSectionIds);

            if (nextSectionIds.has(sectionKey)) {
                nextSectionIds.delete(sectionKey);
            } else {
                nextSectionIds.add(sectionKey);
            }

            updateParams(chapterId, nextSectionIds);
        },
        [openSectionIds, updateParams],
    );

    const syncFromLoadedChapters = useCallback(
        (chapterIds: string[]) => {
            if (!openChapterId) {
                return;
            }

            if (!chapterIds.includes(openChapterId)) {
                updateParams(null, new Set());
            }
        },
        [openChapterId, updateParams],
    );

    const pruneSectionsForChapter = useCallback(
        (chapterId: string, validSectionIds: string[]) => {
            if (openChapterId !== chapterId) {
                return;
            }

            const validKeys = new Set(validSectionIds.map((sectionId) => toSectionKey(chapterId, sectionId)));
            const pruned = new Set([...openSectionIds].filter((key) => validKeys.has(key)));

            if (pruned.size !== openSectionIds.size) {
                updateParams(chapterId, pruned);
            }
        },
        [openChapterId, openSectionIds, updateParams],
    );

    return {
        openChapterId,
        openSectionIds,
        handleToggleChapter,
        handleToggleSection,
        syncFromLoadedChapters,
        pruneSectionsForChapter,
    };
};
